import React, { Suspense, lazy } from 'react';
import { EnhancedSkeleton, CardSkeleton } from './EnhancedAnimations';

// Loading fallback component
const LoadingFallback = ({ type = 'card', count = 1 }) => {
  if (type === 'card') {
    return (
      <div className="space-y-4">
        {Array.from({ length: count }).map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (type === 'page') {
    return (
      <div className="space-y-6">
        <EnhancedSkeleton type="title" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <EnhancedSkeleton type="card" />
          <EnhancedSkeleton type="card" />
          <EnhancedSkeleton type="card" />
        </div>
        <EnhancedSkeleton type="card" className="h-64" />
      </div>
    );
  }

  return <EnhancedSkeleton type={type} />;
};

// Lazy load wrapper with error boundary
export const lazyLoad = (importFunc, fallback = <LoadingFallback type="card" />) => {
  const LazyComponent = lazy(importFunc);
  
  return (props) => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

// Lazy loaded components
export const LazyDocumentManager = lazyLoad(
  () => import('./DocumentManagerEnhanced'),
  <LoadingFallback type="page" />
);

export const LazyCommunicationCenter = lazyLoad(
  () => import('./CommunicationCenterEnhanced'),
  <LoadingFallback type="page" />
);

export const LazyCustomAssessments = lazyLoad(
  () => import('./CustomAssessmentTemplates'),
  <LoadingFallback type="page" />
);

export const LazyEnterpriseFeatures = lazyLoad(
  () => import('./EnterpriseFeatures'),
  <LoadingFallback type="page" />
);

export const LazyAdvancedReporting = lazyLoad(
  () => import('./AdvancedReporting'),
  <LoadingFallback type="page" />
);

export const LazyClinicalReports = lazyLoad(
  () => import('./ClinicalReports'),
  <LoadingFallback type="page" />
);

export const LazySmartDataFeatures = lazyLoad(
  () => import('./SmartDataFeatures'),
  <LoadingFallback type="page" />
);

export const LazyPredictiveAnalytics = lazyLoad(
  () => import('./PredictiveAnalytics'),
  <LoadingFallback type="page" />
);

export default LoadingFallback;
